import { Link } from 'react-router-dom'
import { useState } from 'react'
import { Menu, X, Sun, Moon } from 'lucide-react'
import { useAuth, useTheme, useClientConfig } from '@workspace/public-utils'

const Header = () => {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const config = useClientConfig()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)

  return (
    <header className="bg-white dark:bg-gray-900 shadow-lg border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: config.logoIconColor }}>
              <span className="text-white font-black text-lg">{config.logoIconText}</span>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center space-x-1">
                <span className="text-xl font-black" style={{ color: config.logoIconColor }}>{config.brandNameEn}</span>
              </div>
              <span className="text-base font-bold text-gray-800 dark:text-gray-200">{config.brandName}</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-16" data-testid="desktop-nav">
            <Link to="/results" className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors">
              당첨결과
            </Link>
            <Link to="/analysis" className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors">
              번호분석
            </Link>
            <Link to="/plans" className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors">
              구독플랜
            </Link>
            <Link to="/faq" className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors">
              고객지원
            </Link>
          </nav>

          {/* Desktop Auth buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
              data-testid="dark-mode-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            {user ? (
              <div className="flex items-center space-x-4">
                <Link 
                  to="/dashboard" 
                  className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors"
                >
                  마이페이지
                </Link>
                <button 
                  onClick={logout}
                  className="btn-outline text-sm px-4 py-2"
                >
                  로그아웃
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link 
                  to="/auth/login" 
                  className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors"
                >
                  로그인
                </Link>
                <Link 
                  to="/auth/register" 
                  className="btn-primary text-sm px-6 py-2"
                >
                  회원가입
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile dark mode toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            <button
              onClick={toggleMobileMenu}
              className="text-gray-600 dark:text-gray-300 hover:text-happiness-primary transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-4">
            <div className="flex flex-col space-y-4">
              <Link 
                to="/results" 
                className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors px-2 py-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                당첨결과
              </Link>
              <Link 
                to="/analysis" 
                className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors px-2 py-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                번호분석
              </Link>
              <Link 
                to="/plans" 
                className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors px-2 py-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                구독플랜
              </Link>
              <Link 
                to="/faq" 
                className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors px-2 py-1"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                고객지원
              </Link>
              
              {user ? (
                <div className="flex flex-col space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Link 
                    to="/dashboard" 
                    className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors px-2 py-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    마이페이지
                  </Link>
                  <button 
                    onClick={() => {
                      logout()
                      setIsMobileMenuOpen(false)
                    }}
                    className="btn-outline text-sm px-4 py-2 w-full"
                  >
                    로그아웃
                  </button>
                </div>
              ) : (
                <div className="flex flex-col space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Link 
                    to="/auth/login" 
                    className="text-gray-700 dark:text-gray-300 hover:text-happiness-primary font-medium transition-colors px-2 py-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    로그인
                  </Link>
                  <Link 
                    to="/auth/register" 
                    className="btn-primary text-sm px-6 py-2 w-full text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    회원가입
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header